import type { SuspenseQueryHookOptions } from "@apollo/client";
import { skipToken } from "@apollo/client";
import { useMutationHandler } from "@sinch/hooks/useMutationHandler";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { useSuspenseQueryHandler } from "@sinch/hooks/useSuspenseQueryHandler";
import { useCallback } from "react";
import { CreateRateRounding, DeleteRateRounding, EditRateRounding, RateRounding, RateRoundingList } from "./queries.graphql";
import type {
  CreateRateRoundingMutation,
  CreateRateRoundingMutationVariables,
  DeleteRateRoundingMutation,
  DeleteRateRoundingMutationVariables,
  EditRateRoundingMutation,
  EditRateRoundingMutationVariables,
  RateRoundingListQuery,
  RateRoundingQuery,
  RateRoundingQueryVariables,
} from "./queries.types";

interface UseRateRoundingProps {
  /** Load one definition with its usage (dialog); without it the list is loaded instead */
  id?: string | null;
  queryOptions?: Omit<SuspenseQueryHookOptions<RateRoundingQuery, RateRoundingQueryVariables>, "variables">;
}

/**
 * Rounding definitions: list, single item with usage, mutations and name lookup
 */
export const useRateRounding = (props?: UseRateRoundingProps) => {
  const id = props?.id;

  const [create, { loading: createLoading }] = useMutationHandler<CreateRateRoundingMutation, CreateRateRoundingMutationVariables>(
    CreateRateRounding
  );
  const [edit, { loading: editLoading }] = useMutationHandler<EditRateRoundingMutation, EditRateRoundingMutationVariables>(EditRateRounding);
  const [remove, { loading: removeLoading }] = useMutationHandler<DeleteRateRoundingMutation, DeleteRateRoundingMutationVariables>(
    DeleteRateRounding
  );

  const { data } = useSuspenseQueryHandler<RateRoundingQuery, RateRoundingQueryVariables>(
    RateRounding,
    id ? { id } : undefined,
    id ? (props?.queryOptions ?? {}) : skipToken
  );
  const { data: list, loading: listLoading, refetch: refetchList } = useQueryHandler<RateRoundingListQuery>(RateRoundingList, undefined, {
    skip: !!id,
  });

  const roundings = list?.rateRoundings ?? [];

  const getRounding = useCallback(
    (roundingId: string | null | undefined) =>
      roundingId ? (roundings.find((rounding) => rounding.id === roundingId) ?? null) : null,
    [roundings]
  );

  const getLabel = useCallback(
    (roundingId: string | null | undefined): string | null => getRounding(roundingId)?.name ?? null,
    [getRounding]
  );

  return {
    create,
    edit,
    remove,
    loading: createLoading || editLoading || removeLoading,
    /** The list query is still in flight (false when a single definition is loaded instead) */
    listLoading,
    rateRounding: data?.rateRounding ?? null,
    roundings,
    refetchList,
    getRounding,
    getLabel,
  };
};
